import type { ResourceMetadata } from "@istari-frontend/resource/model";
import { keepPreviousData, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { toResourceDirectoryLabel } from "~/resource/data-source/adapter/v3/to-resource-directory-label";
import { resourceQueryOptions } from "~/resource/data-source/api/resource-query-options";
import type { LookupResult } from "~/shared/lookup-result";

export function useResourceMetadata(resourceIds: string[]): Map<string, LookupResult<ResourceMetadata>> {
  const idsKey = [...new Set(resourceIds.filter(Boolean))].sort().join("\0");
  const ids = useMemo(() => (idsKey ? idsKey.split("\0") : []), [idsKey]);
  const cache = useQueryClient();
  const batch = useQuery({
    ...resourceQueryOptions.metadataBatch(ids, cache),
    placeholderData: keepPreviousData,
  });
  const byId = useMemo(() => new Map(batch.data?.map((resource) => [resource.resourceId, resource])), [batch.data]);
  // Listing omits inaccessible IDs; by-ID fallback isolates omissions and whole-batch failures.
  const fallbacks = useQueries({
    queries: ids.map((id) => ({
      ...resourceQueryOptions.byId(id),
      enabled: !batch.isFetching && (batch.isError || (batch.isSuccess && !byId.has(id))),
    })),
    combine: (results) =>
      Object.fromEntries(
        results.map((result, index) => [
          ids[index],
          {
            data: result.data,
            error: result.error,
            isLoading: result.isLoading,
            isError: result.isError,
          },
        ]),
      ),
  });
  return useMemo(
    () =>
      new Map(
        ids.map((id) => {
          const fallback = fallbacks[id];
          const resource = batch.isError ? (fallback.data ?? byId.get(id)) : (byId.get(id) ?? fallback.data);
          return [
            id,
            {
              data: resource
                ? {
                    name: resource.name ?? null,
                    directoryLabel: toResourceDirectoryLabel(resource),
                    resourceType: resource.resourceType,
                    createdById: resource.createdById,
                  }
                : undefined,
              error: fallback.error,
              isLoading: (!resource && batch.isFetching) || fallback.isLoading,
              isError: fallback.isError,
            },
          ];
        }),
      ),
    [byId, batch.isFetching, batch.isError, fallbacks, ids],
  );
}
